import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Typography,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Form, Formik } from 'formik';
import clsx from 'clsx';

import yup from '../../../utility/yup';
import { selectIsAuthenticated } from '../../../store/modules/auth/selectors';
import { useTitle } from '../../../hooks';
import { RegisterPayload } from '../../../store/modules/registration/types';
import {
  FormInput,
  FormPasswordInput,
  FormSubmit,
  Link, PagesTitle,
  Space,
  IntercomComponent,
  FormPromotionCodeInput,
} from '../../../components';
import logo from '../../../assets/img/ph/logo-blue.svg';
import FormSwitch from '../../../components/form-switch/FormSwitch';
import {
  selectRegister,
  selectRegistrationForm,
} from '../../../store/modules/registration/selectors';
import { passwordSchema } from '../../../components/password-error/PasswordError';
import { registerRequest } from '../../../store/modules/registration/actions';
import { selectInvitation } from '../../../store/modules/app/selectors';
import { Links } from '../../../utility/links';
import PageContainer from '../PageContainer';
import FormSelect from '../../../components/form-select/FormSelect';
import { selectProductList, selectProductOptions } from '../../../store/modules/products/selectors';
import { getProductListRequest } from '../../../store/modules/products/actions';
import useStyles from './styles';

const registerSchema = yup.object().shape({
  productCode: yup.string().required('Subscription type is required'),
  firstName: yup.string()
    .matches(/^[\p{Alpha}\s+(.\-`'&)]+$/u, 'First Name has a wrong symbols')
    .required('First Name is required.'),
  lastName: yup.string()
    .matches(/^[\p{Alpha}\s+(.\-`'&)]+$/u, 'Last Name has a wrong symbols')
    .required('Last Name is required.'),
  email: yup.string()
    .email('This must be a valid email.')
    .required('Email is required.'),
  password: passwordSchema,
  agree: yup.bool().oneOf([true]).required(),
  promo_code: yup.string().min(8).max(12)
    .matches(
      /^.[a-zA-Z0-9]+$/,
      {
        message: 'Only alphanumeric characters',
        excludeEmptyString: true,
      },
    )
    .trim()
    .label('Code'),
});

type FormPayload = RegisterPayload & {
  agree: boolean;
};

type Props = {
  location?: History;
};
const Register = ({ location }: Props) => {
  useTitle('Register');

  const classes = useStyles();
  const selectedProductCode = location?.state?.productCode;
  const invitation = useSelector(selectInvitation);
  const { loading, error } = useSelector(selectRegister);
  const {
    firstName,
    lastName,
    email,
    password,
    productCode,
    promoCode,
  } = useSelector(selectRegistrationForm);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const subscriptionOptions = useSelector(selectProductOptions);
  const { data: fullProductList } = useSelector(selectProductList);

  useEffect(() => {
    if (!fullProductList) {
      dispatch(getProductListRequest());
    }
  }, [dispatch, fullProductList]);

  const register = useCallback(({ agree, ...registerPayload }: FormPayload) => {
    dispatch(registerRequest(registerPayload));
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(push('/dashboard/home'));
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (!loading) {
      if (error) {
        dispatch(push('/pages/register-error'));
      }
    }
  }, [dispatch, loading, email, error]);

  const initialValues = useMemo(() => ({
    productCode: selectedProductCode || productCode,
    firstName,
    lastName,
    email: invitation?.email || email,
    password,
    agree: false,
    promoCode,
  }), [firstName, lastName, email, password, productCode, selectedProductCode, invitation, promoCode]);

  // const subscriptions = subscriptionList
  //   .map((item): GenericItem<string> => ({ id: item.product_code, title: item.title }));

  return (
    <PageContainer>
      <div className={classes.root}>
        <img
          src={logo}
          alt="logo"
          className={classes.logo}
        />
        <PagesTitle>Create your PitchHub Account</PagesTitle>

        <Typography variant="subtitle1" className={classes.subtitle}>
          Already have an account?
          <Space />
          <Link to="/pages/login">
            Log In.
          </Link>
        </Typography>
        <Formik<FormPayload>
          initialValues={initialValues}
          onSubmit={register}
          validationSchema={registerSchema}
          validateOnMount
        >
          <Form>
            <FormSelect
              label="Plan"
              name="productCode"
              items={subscriptionOptions}
              useBlankSelectBeforeInit
            />
            <FormInput
              className={classes.control}
              fullWidth
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
            />
            <FormInput
              className={classes.control}
              fullWidth
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
            />
            <FormInput
              className={classes.control}
              fullWidth
              label="Email Address"
              name="email"
              autoComplete="email"
            />
            <FormPasswordInput
              className={classes.control}
              fullWidth
              name="password"
              label="Password"
              autoComplete="new-password"
            />
            <FormPromotionCodeInput
              className={classes.control}
              name="promoCode"
              label="Promotion Code"
              autoCheck
            />
            <div className={classes.policyContainer}>
              <FormSwitch
                label=""
                name="agree"
                classes={{
                  root: classes.policySwitch,
                  label: classes.controlLabel,
                }}
              />
              <div className={classes.policy}>
                <span>
                I agree to&nbsp;
                  <Link to={Links.policy} defaultAction target="_blank">
                    PitchHub&apos;s Terms of Use and Privacy Policy
                  </Link>
                </span>
              </div>
            </div>

            <FormSubmit
              className={clsx(classes.submit, classes.control)}
              variant="contained"
              color="primary"
              disabled={loading}
            >
                Sign up
            </FormSubmit>
          </Form>
        </Formik>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Questions?
          <Space />
          <Link to={Links.support} defaultAction target="_blank">
            Find Help.
          </Link>
        </Typography>
        <IntercomComponent />
      </div>
    </PageContainer>
  );
};

export default Register;
